import type { KicadPCB } from "../../vendor/kicanvas/src/kicad/board";
import { RelockEngine } from "./relock.js";
import { generateRegistrationMasks, type RegistrationMask } from "./mask-generator";

type BoardSide = "F" | "B";
type Rect = { x: number; y: number; w: number; h: number };

export type BrowserRegistrationView = {
  id?: string;
  label?: string;
  side: BoardSide;
  photo: string;
  H: number[];
  render_bbox: Rect;
  match?: Record<string, unknown>;
};

const COARSE_SCALES = [0.30, 0.40, 0.50, 0.60];
const FULL_SCALES = [0.25, 0.30, 0.35, 0.40, 0.45, 0.50, 0.55, 0.60, 0.65, 0.70, 0.80, 0.90, 1.0];

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`could not load photo ${url}`));
    image.src = url;
  });
}

function modelUrl(name: string): string {
  return new URL(`./models/${name}`, document.baseURI).href;
}

async function matchPhoto(
  engine: RelockEngine,
  mask: RegistrationMask,
  photo: HTMLImageElement,
  label: string,
  onStatus?: (message: string) => void,
) {
  engine.setRenderImage(mask.canvas);
  const status = (message: string) => onStatus?.(`${label}: ${message}`);
  status("matching browser-generated mask…");
  let result = await engine.lockFrame(photo, status, {
    scales: COARSE_SCALES,
    maxSide: 1200,
    topK: 4096,
    minCos: 0.8,
    minScore: 8,
    minInliers: 12,
  });
  if (!result) {
    status("retrying full scale search…");
    result = await engine.lockFrame(photo, status, {
      scales: FULL_SCALES,
      maxSide: 1200,
      topK: 4096,
      minCos: 0.8,
      minScore: 8,
      minInliers: 12,
    });
  }
  if (!result) throw new Error(`${label}: browser registration failed`);
  return result;
}

export async function registerExampleInBrowser(
  board: KicadPCB,
  views: BrowserRegistrationView[],
  onStatus?: (message: string) => void,
): Promise<BrowserRegistrationView[]> {
  const masks = await generateRegistrationMasks(board, onStatus);
  onStatus?.("loading XFeat WebGPU model…");
  const engine = new RelockEngine({
    models: {
      learned_rgb: modelUrl("xfeat_learn_steer_rgb01.onnx"),
      steerer: modelUrl("steerer.json"),
    },
    relock: {
      maxSideInitial: 1200,
      topKInitial: 4096,
      minCos: 0.8,
      minScore: 8,
      minInliers: 12,
    },
  });
  const executionProvider = await engine.init();
  onStatus?.(`XFeat ready (${executionProvider})`);

  const output: BrowserRegistrationView[] = [];
  const debugResults: any[] = [];
  for (const view of views) {
    const photo = await loadImage(view.photo);
    const mask = masks[view.side];
    const result = await matchPhoto(engine, mask, photo, view.label ?? view.id ?? view.side, onStatus);
    output.push({
      ...view,
      H: Array.from(result.H_display),
      render_bbox: mask.render_bbox,
      match: {
        source: "browser-xfeat-webgpu",
        recipe: mask.metadata.recipe,
        execution_provider: executionProvider,
        scale: result.scale,
        inliers: result.inliers,
        matches: result.matches,
        spread: result.spread,
        score: result.score,
        steer_rotation: result.steerRot,
      },
    });
    debugResults.push({ side: view.side, result, mask: mask.metadata });
    onStatus?.(`${view.label ?? view.side}: registered with ${result.inliers} inliers`);
  }

  (window as any).__kistoneBrowserRegistration = { masks, results: debugResults };
  return output;
}
